
import { Component, Prop, mixins } from 'nuxt-property-decorator';
import {
  PlanDto,
  UpgradeTransactionDto,
  getPlanEndCountdown,
  UpgradeTransactionState,
  PackageTier,
} from 'fourwaves-shared';
import { Alert } from 'fourwaves-shared/components';
import UpgradeButton from '~/components/upgrade/UpgradeButton.vue';
import { OrganizationMixin } from '~/mixins';
import { UpgradeCheckoutEvents } from '../upgrade/types';
import { TranslateResult } from 'fourwaves-shared/src/types/i18n';

@Component({
  components: {
    Alert,
    UpgradeButton,
  },
})
export default class OrganizationPlanAlert extends mixins(OrganizationMixin) {
  @Prop({ default: Array }) readonly orgPlans!: PlanDto[];
  @Prop({ default: null }) readonly overdueTransactions!: UpgradeTransactionDto[] | null;
  @Prop(Boolean) readonly banner!: boolean;

  isDismissed = false;
  cookieName = 'org_plan_alert-dismissed';

  plans: PlanDto[] = [];
  transactions: UpgradeTransactionDto[] = [];

  get currentPlan(): PlanDto | null {
    if (!this.organization) return null;
    return this.plans.find(({ id }) => id === this.organization?.currentPlanId) || null;
  }

  get programmedPlan(): PlanDto | null {
    return (
      this.plans.find(({ startDate }) =>
        this.$moment(startDate).isSameOrAfter(this.$moment(this.currentPlan?.endDate)),
      ) || null
    );
  }

  get hasPaymentOverdue(): boolean {
    if (!this.transactions) return false;
    return !!this.transactions.length;
  }

  get planEndCountdown(): number {
    return getPlanEndCountdown(this.currentPlan);
  }

  get hasPayingPlan(): boolean {
    if (this.currentPlan?.package.tier !== PackageTier.Free || this.currentPlan?.addOns.length) return true;
    return false;
  }

  get isPlanEndingIn30Days(): boolean {
    if (!this.hasPayingPlan) return false;
    return !!(this.planEndCountdown && this.planEndCountdown <= 30);
  }

  get isAlertVisible(): boolean {
    if (this.banner && this.isDismissed) return false;
    return this.hasPaymentOverdue || (this.isPlanEndingIn30Days && !this.programmedPlan);
  }

  get title(): TranslateResult {
    if (this.hasPaymentOverdue) {
      return this.$t('plans.overdue_plan_alert.title');
    }
    return this.$tc('plans.ending_plan_alert.title', this.planEndCountdown);
  }

  get message(): TranslateResult {
    const userTimezone = this.$moment.tz.guess(true);
    if (this.hasPaymentOverdue) {
      const date = this.$moment(this.getOldestTransactionDate(this.transactions)).tz(userTimezone).format('LL');
      return this.$t('plans.overdue_plan_alert.message', { date });
    }
    const date = this.$moment(this.currentPlan?.endDate).tz(userTimezone).format('LL') || '';
    return this.$t('plans.ending_plan_alert.message', {
      date,
    });
  }

  get invoiceTo(): { name: string; params?: Record<string, string | number> } {
    return this.transactions.length > 1
      ? {
          name: 'organizations-id-plan-transactions',
        }
      : { name: 'organizations-id-invoices-transactionId', params: { transactionId: this.transactions[0].id } };
  }

  async created() {
    this.isDismissed = !!this.$cookies.get(this.cookieName);
    this.$nuxt.$on(UpgradeCheckoutEvents.Completed, this.refreshData);
    this.plans = this.orgPlans.length ? this.orgPlans : await this.fetchPlans();
    this.transactions = this.overdueTransactions || (await this.fetchTransactions()).items;
  }

  beforeDestroy() {
    this.$nuxt.$off(UpgradeCheckoutEvents.Completed, this.refreshData);
  }

  public async refreshData() {
    this.plans = await this.fetchPlans();
    this.transactions = (await this.fetchTransactions()).items;
  }

  public getOldestTransactionDate(transactions: UpgradeTransactionDto[]): string {
    const dates = transactions
      .map(transaction => transaction.gracePeriodEndDate)
      .sort((a, b) => (new Date(b) < new Date(a) ? 1 : -1));
    return dates[0];
  }

  public async fetchPlans(): Promise<PlanDto[]> {
    if (!this.organization) return [];
    return await this.$api.getPlans({ organizationId: this.organization.id });
  }

  public async fetchTransactions() {
    return await this.$api.getUpgradeTransactions({
      organizationId: this.$route.params.id,
      states: [UpgradeTransactionState.Overdue],
      pageSize: 50,
    });
  }

  public dismissHandler() {
    this.$cookies.set(this.cookieName, true, { expires: this.$moment(new Date()).add(5, 'days').toDate() });
    this.isDismissed = true;
  }
}
